import type { NetworkEvent, NetworkResourceType, NetworkTiming } from '@logclip/types';
import type { InstrumentCtx, Teardown } from './context.js';
import { maskString, redactUrl } from './mask.js';
import { tokenBucket } from './ratelimit.js';
import { applyFetchTrace, newTrace, traceIdOf, traceTarget } from './trace.js';
import { uuid } from './util.js';

const BODY_CAP = 4 * 1024; // 4 KB body preview cap
const NET_RATE = 50; // sustained network events/sec per page
const NET_BURST = 100; // burst capacity
// Response/request header allowlist — everything else is dropped.
const HEADER_ALLOW = new Set([
  'content-type',
  'content-length',
  'accept',
  'accept-encoding',
  'cache-control',
  'etag',
  'last-modified',
  'content-encoding',
  'x-request-id',
  'x-correlation-id',
  'traceparent',
  'server',
  'vary',
]);

function classifyHttp(status: number): string | undefined {
  if (status === 0) return 'network';
  if (status >= 500) return 'http_5xx';
  if (status >= 400) return 'http_4xx';
  return undefined;
}

function pickHeaders(
  redact: string[],
  entries: Iterable<[string, string]>,
): Record<string, string> {
  const out: Record<string, string> = {};
  const redactSet = new Set(redact.map((h) => h.toLowerCase()));
  for (const [k, v] of entries) {
    const key = k.toLowerCase();
    if (redactSet.has(key)) out[key] = 'redacted';
    else if (HEADER_ALLOW.has(key)) out[key] = v;
  }
  return out;
}

function bodyPreview(
  text: string | undefined,
  contentType: string | undefined,
): NetworkEvent['requestBody'] {
  if (text == null) return undefined;
  // Pre-cut giant bodies (2× slack) so masking never regex-scans a multi-MB string.
  const cut = text.length > BODY_CAP * 2 ? text.slice(0, BODY_CAP * 2) : text;
  const masked = maskString(cut);
  const truncated = cut !== text || masked.length > BODY_CAP;
  return {
    contentType,
    preview: truncated ? masked.slice(0, BODY_CAP) : masked,
    truncated,
    redacted: masked !== cut,
  };
}

/** Read at most `cap`+1 bytes of a Response body via its stream reader (then
 *  cancel), so a multi-MB response is never fully buffered just to keep a 4 KB
 *  preview. `overflow` = the body had more bytes than `cap`. Falls back to
 *  text() where a stream reader isn't available (older fetch polyfills). */
async function readTextCapped(res: Response, cap: number): Promise<{ text: string; overflow: boolean }> {
  const body = res.body;
  if (!body || typeof body.getReader !== 'function') {
    return { text: await res.text(), overflow: false };
  }
  const reader = body.getReader();
  const decoder = new TextDecoder(); // Response.text() decodes UTF-8 — match it
  let text = '';
  let bytes = 0;
  let overflow = false;
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done || !value) {
        text += decoder.decode(); // flush any trailing multi-byte sequence
        break;
      }
      const remaining = cap + 1 - bytes;
      if (value.byteLength >= remaining) {
        // Keep only up to cap+1 bytes total — the +1 detects truncation.
        text += decoder.decode(value.subarray(0, remaining), { stream: true });
        overflow = true;
        break;
      }
      text += decoder.decode(value, { stream: true });
      bytes += value.byteLength;
    }
  } finally {
    // Release the tee'd clone branch so it doesn't buffer the rest of the response.
    void reader.cancel().catch(() => {});
  }
  return { text, overflow };
}

function perfNow(): number {
  return typeof performance !== 'undefined' ? performance.now() : 0;
}

/** Resource Timing `entry.name` is always absolute; requests may be issued with a
 *  relative URL. Absolutize (query preserved) so the two can be matched. */
function absUrl(raw: string): string {
  try {
    return new URL(raw, typeof location !== 'undefined' ? location.href : undefined).href;
  } catch {
    return raw;
  }
}

/** Extract the DNS/TCP/TLS/TTFB/Download breakdown from a matched Resource Timing
 *  entry, layered onto the SDK-measured `base` (startT/endT/duration). Phases that
 *  are zero — a reused keep-alive connection, or a cross-origin resource without a
 *  `Timing-Allow-Origin` header (browser zeroes them for privacy) — are left
 *  undefined so the player shows "—" rather than a misleading 0. */
function phasesFrom(m: PerformanceResourceTiming, base: NetworkTiming): NetworkTiming {
  const t: NetworkTiming = { ...base };
  const r = (n: number) => Math.round(n);
  // Every phase is guarded on its START marker being > 0. A cross-origin resource
  // without `Timing-Allow-Origin` has its start markers (domainLookupStart,
  // connectStart, secureConnectionStart, requestStart, responseStart) zeroed by the
  // browser while responseEnd stays real — so a bare `end > start` check would
  // compute a garbage phase (e.g. download = responseEnd − 0). Requiring start > 0
  // omits those phases instead.
  if (m.domainLookupStart > 0 && m.domainLookupEnd > m.domainLookupStart)
    t.dns = r(m.domainLookupEnd - m.domainLookupStart);
  if (m.connectStart > 0 && m.connectEnd > m.connectStart) t.tcp = r(m.connectEnd - m.connectStart);
  if (m.secureConnectionStart > 0 && m.connectEnd > m.secureConnectionStart)
    t.tls = r(m.connectEnd - m.secureConnectionStart);
  if (m.requestStart > 0 && m.responseStart > m.requestStart)
    t.ttfb = r(m.responseStart - m.requestStart);
  if (m.responseStart > 0 && m.responseEnd > m.responseStart)
    t.download = r(m.responseEnd - m.responseStart);
  return t;
}

/** Pick the Resource Timing entry that best corresponds to a request that started
 *  at `perfStart` (performance-timeline ms): same URL, started at ~the same time.
 *  Guards against mis-attributing timing when the same URL is hit repeatedly. */
function matchEntry(
  name: string,
  perfStart: number,
  entries: PerformanceResourceTiming[],
): PerformanceResourceTiming | undefined {
  let best: PerformanceResourceTiming | undefined;
  let bestDelta = Infinity;
  for (const e of entries) {
    if (e.name !== name) continue;
    if (e.startTime < perfStart - 4) continue; // started before our request → not ours
    const d = e.startTime - perfStart;
    if (d < bestDelta) {
      bestDelta = d;
      best = e;
    }
  }
  return best;
}

interface PendingTiming {
  name: string; // absolute URL, to match against PerformanceResourceTiming.name
  perfStart: number;
  base: NetworkTiming;
  resolve: (t: NetworkTiming) => void;
  timer: ReturnType<typeof setTimeout>;
}

/** How long to wait for a Resource Timing entry to be published before giving up
 *  and emitting with duration-only timing. The browser adds the entry to the
 *  performance buffer *slightly after* fetch/XHR completion, so a synchronous
 *  lookup at completion almost always misses it — hence this short async wait. */
const TIMING_WAIT_MS = 300;

interface TimingResolver {
  resolve(rawUrl: string, perfStart: number, base: NetworkTiming): Promise<NetworkTiming>;
  flushPending(): void;
  teardown(): void;
}

/** Resolves the phase breakdown for a fetch/XHR asynchronously. A single shared
 *  PerformanceObserver watches for resource entries as they land and settles the
 *  matching pending request; unmatched requests settle on a short timeout. */
function createTimingResolver(): TimingResolver {
  const pending: PendingTiming[] = [];
  let obs: PerformanceObserver | undefined;

  const stopObs = () => {
    if (obs) {
      try {
        obs.disconnect();
      } catch {
        /* ignore */
      }
      obs = undefined;
    }
  };

  const settle = (p: PendingTiming, t: NetworkTiming) => {
    const i = pending.indexOf(p);
    if (i === -1) return; // already settled
    pending.splice(i, 1);
    clearTimeout(p.timer);
    p.resolve(t);
    if (pending.length === 0) stopObs();
  };

  const consume = (entries: PerformanceEntryList) => {
    for (const e of entries as PerformanceResourceTiming[]) {
      let best: PendingTiming | undefined;
      let bestDelta = Infinity;
      for (const p of pending) {
        if (e.name !== p.name || e.startTime < p.perfStart - 4) continue;
        const d = e.startTime - p.perfStart;
        if (d < bestDelta) {
          bestDelta = d;
          best = p;
        }
      }
      if (best) settle(best, phasesFrom(e, best.base));
    }
  };

  const ensureObserver = () => {
    if (obs || typeof PerformanceObserver === 'undefined') return;
    try {
      obs = new PerformanceObserver((list) => consume(list.getEntries()));
      obs.observe({ type: 'resource', buffered: false });
    } catch {
      obs = undefined;
    }
  };

  return {
    resolve(rawUrl, perfStart, base) {
      if (typeof performance === 'undefined') return Promise.resolve(base);
      const name = absUrl(rawUrl);
      // Fast path: the entry may already be buffered (cached / same-tick resources).
      try {
        const now = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
        const immediate = matchEntry(name, perfStart, now);
        if (immediate) return Promise.resolve(phasesFrom(immediate, base));
      } catch {
        /* ignore */
      }
      if (typeof PerformanceObserver === 'undefined') return Promise.resolve(base);
      // Slow path: wait for the entry to be published (or time out).
      return new Promise<NetworkTiming>((resolve) => {
        const p: PendingTiming = {
          name,
          perfStart,
          base,
          resolve,
          timer: setTimeout(() => settle(p, base), TIMING_WAIT_MS),
        };
        pending.push(p);
        ensureObserver();
      });
    },
    // Force-settle everything still in flight (with duration-only timing) so their
    // events are emitted into the transport buffer before an unload beacon fires.
    flushPending() {
      for (const p of [...pending]) settle(p, p.base);
    },
    teardown() {
      this.flushPending();
      stopObs();
    },
  };
}

/** A rate-gated NetworkEvent emitter shared by all capture paths on the page. */
type EmitNet = (ev: NetworkEvent) => void;

export function instrumentNetwork(ctx: InstrumentCtx): Teardown {
  const timing = createTimingResolver();
  // Per-page event throttle — a polling loop / request storm must not flood the
  // buffer. Dropped events are counted and reported on the next captured one.
  const bucket = tokenBucket(NET_RATE, NET_BURST);
  let dropped = 0;
  const emitNet: EmitNet = (ev) => {
    if (!bucket.take()) {
      dropped++;
      return;
    }
    if (dropped > 0) {
      ev.droppedEvents = dropped;
      dropped = 0;
    }
    ctx.emit(ev);
  };
  // On page hide, settle any request whose Resource Timing hasn't arrived yet so
  // its event lands in the transport buffer before the unload flush.
  const onPageHide = () => timing.flushPending();
  const onVisibility = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') timing.flushPending();
  };
  if (typeof window !== 'undefined') window.addEventListener('pagehide', onPageHide);
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);

  const teardowns: Teardown[] = [];
  teardowns.push(wrapFetch(ctx, timing, emitNet));
  teardowns.push(wrapXhr(ctx, timing, emitNet));
  teardowns.push(wrapBeacon(ctx, emitNet));
  teardowns.push(observeResources(ctx, emitNet));
  teardowns.push(() => {
    if (typeof window !== 'undefined') window.removeEventListener('pagehide', onPageHide);
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
    timing.teardown();
  });
  return () => teardowns.forEach((t) => t());
}

// ───────────────────────────── fetch ────────────────────────────────
function wrapFetch(ctx: InstrumentCtx, tr: TimingResolver, emitNet: EmitNet): Teardown {
  const orig = window.fetch;
  if (!orig) return () => {};
  window.fetch = async function (input: RequestInfo | URL, init?: RequestInit) {
    const rawUrl = input instanceof Request ? input.url : String(input);
    // Never record our own telemetry round-trips to the ingest endpoint.
    if (rawUrl.includes('/v1/batch') || rawUrl.includes('/v1/config'))
      return orig.call(window, input as RequestInfo, init);
    const startT = ctx.clock.now();
    const perfStart = perfNow();
    const method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
    const url = redactUrl(rawUrl);
    // Trace-context propagation (best-effort, scoped to first-party hosts). Inject
    // BEFORE reading reqHeaders so the traceparent shows in the captured headers,
    // and stamp the trace id onto the event so replay ↔ backend trace can join.
    let traceId: string | undefined;
    if (traceTarget(rawUrl, ctx.config)) {
      try {
        const inj = applyFetchTrace(input, init);
        input = inj.input;
        init = inj.init;
        traceId = inj.traceId || undefined;
      } catch {
        /* never let propagation break the request */
      }
    }
    const reqHeaders = pickHeaders(
      ctx.config.redactHeaders,
      headerEntries(init?.headers ?? (input instanceof Request ? input.headers : undefined)),
    );

    const emit = (partial: Partial<NetworkEvent>) => {
      const endT = ctx.clock.now();
      const base: NetworkTiming = { startT, endT, duration: endT - startT };
      ctx.markActivity();
      // Resolve the phase breakdown asynchronously — the Resource Timing entry is
      // published just after completion, so a synchronous read would miss it.
      void tr.resolve(rawUrl, perfStart, base).then((timing) => {
        const ev: NetworkEvent = {
          id: uuid(),
          sessionId: ctx.sessionId,
          type: 'network',
          t: startT,
          ts: ctx.clock.wall(),
          initiator: 'fetch',
          method,
          url,
          status: 0,
          ok: false,
          resourceType: 'fetch',
          requestHeaders: reqHeaders,
          traceId,
          timing,
          ...partial,
        };
        emitNet(ev);
      });
    };

    try {
      const res = await orig.call(window, input as RequestInfo, init);
      const status = res.status;
      const respHeaders = pickHeaders(ctx.config.redactHeaders, res.headers.entries());
      const len = res.headers.get('content-length');
      let responseBody: NetworkEvent['responseBody'];
      if (ctx.config.captureNetworkBodies && isTexty(res.headers.get('content-type'))) {
        try {
          // Stream-read the clone up to the preview cap — never buffer a huge body.
          const { text, overflow } = await readTextCapped(res.clone(), BODY_CAP);
          responseBody = bodyPreview(text, res.headers.get('content-type') || undefined);
          if (responseBody && overflow) responseBody.truncated = true;
        } catch {
          /* ignore */
        }
      }
      emit({
        status,
        ok: res.ok,
        responseSize: len ? Number(len) : undefined,
        responseHeaders: respHeaders,
        requestBody: ctx.config.captureNetworkBodies
          ? bodyPreview(stringifyBody(init?.body), undefined)
          : undefined,
        responseBody,
        error: classifyHttp(status),
      });
      return res;
    } catch (err) {
      emit({ status: 0, ok: false, error: classifyFetchError(err) });
      throw err;
    }
  } as typeof window.fetch;
  return () => {
    window.fetch = orig;
  };
}

function classifyFetchError(err: unknown): string {
  const name = (err as { name?: string })?.name;
  if (name === 'AbortError') return 'aborted';
  const msg = String((err as { message?: string })?.message || '').toLowerCase();
  if (msg.includes('cors')) return 'cors';
  if (msg.includes('timeout')) return 'timeout';
  return 'network';
}

// ────────────────────────────── XHR ─────────────────────────────────
interface XhrMeta {
  method: string;
  url: string;
  rawUrl: string;
  startT: number;
  perfStart: number;
  reqHeaders: Record<string, string>;
  body?: string;
  traceId?: string;
}

function wrapXhr(ctx: InstrumentCtx, tr: TimingResolver, emitNet: EmitNet): Teardown {
  const XHR = XMLHttpRequest.prototype;
  const origOpen = XHR.open;
  const origSend = XHR.send;
  const origSetHeader = XHR.setRequestHeader;
  const META = new WeakMap<XMLHttpRequest, XhrMeta>();

  XHR.open = function (this: XMLHttpRequest, method: string, url: string | URL, ...rest: unknown[]) {
    const rawUrl = String(url);
    META.set(this, {
      method: String(method).toUpperCase(),
      rawUrl,
      url: redactUrl(rawUrl),
      startT: 0,
      perfStart: 0,
      reqHeaders: {},
    });
    return origOpen.apply(this, [method, url, ...rest] as never);
  } as typeof XHR.open;

  XHR.setRequestHeader = function (this: XMLHttpRequest, name: string, value: string) {
    const meta = META.get(this);
    if (meta) {
      const key = name.toLowerCase();
      if (ctx.config.redactHeaders.map((h) => h.toLowerCase()).includes(key))
        meta.reqHeaders[key] = 'redacted';
      else if (HEADER_ALLOW.has(key)) meta.reqHeaders[key] = value;
    }
    return origSetHeader.apply(this, [name, value] as never);
  } as typeof XHR.setRequestHeader;

  XHR.send = function (this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null) {
    const meta = META.get(this);
    if (meta) {
      meta.startT = ctx.clock.now();
      meta.perfStart = perfNow();
      if (ctx.config.captureNetworkBodies) meta.body = stringifyBody(body);
      // Trace-context propagation (first-party only). setRequestHeader is valid
      // here — after open(), before the real send() below. Our own setRequestHeader
      // patch records it into meta.reqHeaders (traceparent is allowlisted), so a
      // traceparent the APP already set is visible here: respect it (record its id,
      // don't re-set — XHR COMBINES duplicate headers into an invalid "v1, v2").
      if (traceTarget(meta.rawUrl, ctx.config)) {
        try {
          const appSet = meta.reqHeaders['traceparent'];
          if (appSet) {
            meta.traceId = traceIdOf(appSet) ?? undefined;
          } else {
            const tr = newTrace();
            this.setRequestHeader('traceparent', tr.header);
            meta.traceId = tr.traceId;
          }
        } catch {
          /* header rejected (e.g. send already dispatched) — skip, never throw */
        }
      }
      const finish = (error?: string) => {
        const endT = ctx.clock.now();
        const base: NetworkTiming = {
          startT: meta.startT,
          endT,
          duration: endT - meta.startT,
        };
        const status = this.status;
        const respHeaders = parseRawHeaders(ctx, this.getAllResponseHeaders());
        // Response body (texty + readable responseType only), parallel to fetch.
        let responseBody: NetworkEvent['responseBody'];
        if (ctx.config.captureNetworkBodies && isTexty(this.getResponseHeader('content-type'))) {
          try {
            if (this.responseType === '' || this.responseType === 'text') {
              responseBody = bodyPreview(this.responseText, this.getResponseHeader('content-type') || undefined);
            }
          } catch {
            /* opaque/cross-origin response — skip the body */
          }
        }
        const responseSize = safeLen(this);
        ctx.markActivity();
        // Resolve the phase breakdown asynchronously (see wrapFetch).
        void tr.resolve(meta.rawUrl, meta.perfStart, base).then((timing) => {
          const ev: NetworkEvent = {
            id: uuid(),
            sessionId: ctx.sessionId,
            type: 'network',
            t: meta.startT,
            ts: ctx.clock.wall(),
            initiator: 'xhr',
            method: meta.method,
            url: meta.url,
            status,
            ok: status >= 200 && status < 400,
            resourceType: 'xhr',
            requestHeaders: meta.reqHeaders,
            traceId: meta.traceId,
            responseHeaders: respHeaders,
            responseSize,
            requestBody: ctx.config.captureNetworkBodies ? bodyPreview(meta.body, undefined) : undefined,
            responseBody,
            timing,
            error: error || classifyHttp(status),
          };
          emitNet(ev);
        });
      };
      this.addEventListener('loadend', () => finish());
      this.addEventListener('error', () => finish('network'));
      this.addEventListener('timeout', () => finish('timeout'));
      this.addEventListener('abort', () => finish('aborted'));
    }
    return origSend.apply(this, [body] as never);
  } as typeof XHR.send;

  return () => {
    XHR.open = origOpen;
    XHR.send = origSend;
    XHR.setRequestHeader = origSetHeader;
  };
}

function safeLen(xhr: XMLHttpRequest): number | undefined {
  try {
    if (xhr.responseType === '' || xhr.responseType === 'text')
      return new Blob([xhr.responseText]).size;
  } catch {
    /* opaque */
  }
  const len = xhr.getResponseHeader('content-length');
  return len ? Number(len) : undefined;
}

function parseRawHeaders(ctx: InstrumentCtx, raw: string): Record<string, string> {
  const entries: [string, string][] = [];
  for (const line of raw.trim().split(/[\r\n]+/)) {
    const idx = line.indexOf(':');
    if (idx > 0) entries.push([line.slice(0, idx).trim(), line.slice(idx + 1).trim()]);
  }
  return pickHeaders(ctx.config.redactHeaders, entries);
}

// ──────────────────────────── sendBeacon ────────────────────────────
function wrapBeacon(ctx: InstrumentCtx, emitNet: EmitNet): Teardown {
  const orig = navigator.sendBeacon;
  if (!orig) return () => {};
  navigator.sendBeacon = function (url: string | URL, data?: BodyInit | null): boolean {
    const startT = ctx.clock.now();
    let ok = false;
    try {
      ok = orig.call(navigator, url, data);
    } finally {
      // Don't record beacons to our own ingest endpoint.
      const u = String(url);
      if (!u.includes('/v1/batch')) {
        const ev: NetworkEvent = {
          id: uuid(),
          sessionId: ctx.sessionId,
          type: 'network',
          t: startT,
          ts: ctx.clock.wall(),
          initiator: 'beacon',
          method: 'POST',
          url: redactUrl(u),
          status: ok ? 202 : 0,
          ok,
          resourceType: 'other',
          timing: { startT, endT: ctx.clock.now(), duration: 0 },
          error: ok ? undefined : 'network',
        };
        emitNet(ev);
      }
    }
    return ok;
  };
  return () => {
    navigator.sendBeacon = orig;
  };
}

// ─────────────────── PerformanceObserver (resources) ────────────────
const RESOURCE_TYPE_MAP: Record<string, NetworkResourceType> = {
  script: 'script',
  link: 'stylesheet',
  css: 'stylesheet',
  img: 'image',
  image: 'image',
  font: 'font',
  audio: 'media',
  video: 'media',
  track: 'media',
};

function observeResources(ctx: InstrumentCtx, emitNet: EmitNet): Teardown {
  if (typeof PerformanceObserver === 'undefined') return () => {};
  let obs: PerformanceObserver | undefined;
  try {
    obs = new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as PerformanceResourceTiming[]) {
        const it = entry.initiatorType;
        // fetch/XHR are captured by our wrappers — skip to avoid duplicates.
        if (it === 'fetch' || it === 'xmlhttprequest' || it === 'beacon') continue;
        const resourceType = RESOURCE_TYPE_MAP[it] || 'other';
        const startT = ctx.clock.fromPerf(entry.startTime);
        const endT = ctx.clock.fromPerf(entry.responseEnd || entry.startTime);
        const timing: NetworkTiming = {
          startT,
          endT,
          duration: Math.max(0, endT - startT),
          dns: nz(entry.domainLookupEnd - entry.domainLookupStart),
          tcp: nz(entry.connectEnd - entry.connectStart),
          tls: entry.secureConnectionStart ? nz(entry.connectEnd - entry.secureConnectionStart) : undefined,
          ttfb: nz(entry.responseStart - entry.requestStart),
          download: nz(entry.responseEnd - entry.responseStart),
        };
        const ev: NetworkEvent = {
          id: uuid(),
          sessionId: ctx.sessionId,
          type: 'network',
          t: startT,
          ts: ctx.clock.wall(),
          initiator: 'resource',
          method: 'GET',
          url: redactUrl(entry.name),
          status: entry.responseStatus || 200,
          ok: !entry.responseStatus || entry.responseStatus < 400,
          resourceType,
          responseSize: entry.transferSize || entry.encodedBodySize || undefined,
          timing,
        };
        emitNet(ev);
      }
    });
    obs.observe({ type: 'resource', buffered: true });
  } catch {
    return () => {};
  }
  return () => obs?.disconnect();
}

// ────────────────────────────── helpers ─────────────────────────────
function nz(n: number): number | undefined {
  return Number.isFinite(n) && n > 0 ? Math.round(n) : undefined;
}

function isTexty(contentType: string | null): boolean {
  if (!contentType) return false;
  return /json|text|xml|x-www-form-urlencoded/i.test(contentType);
}

function stringifyBody(body: unknown): string | undefined {
  if (body == null) return undefined;
  if (typeof body === 'string') return body;
  if (body instanceof URLSearchParams) return body.toString();
  if (body instanceof FormData) {
    const obj: Record<string, string> = {};
    body.forEach((v, k) => (obj[k] = typeof v === 'string' ? v : '«file»'));
    return JSON.stringify(obj);
  }
  try {
    return JSON.stringify(body);
  } catch {
    return undefined;
  }
}

function headerEntries(
  headers: HeadersInit | Headers | undefined,
): Iterable<[string, string]> {
  if (!headers) return [];
  if (headers instanceof Headers) return headers.entries();
  if (Array.isArray(headers)) return headers as [string, string][];
  return Object.entries(headers);
}
